import React, { useEffect, useState } from 'react';
import { Alert, App, Modal, Select } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { Spinner, useDelayedBusy } from '../loading';
import CardMovePreview, { moveVerb } from './CardMovePreview';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { moveCard } from '../../services/subCollections';
import { apiErrorMessage } from '../../utils/apiErrors';
import { itemTagCarriedRefusal } from '../../utils/itemTagSettings';
import { normalizeId } from '../../utils/ids';

/**
 * A refused move as the sentence the modal shows. An item of the card that is
 * used as a tag, whose tag the move would take off items outside its new
 * family, needs a manager of its collection (owner call B20 — `item_tag_carried`,
 * T4d): said in plain words with the count. Everything else: the API's own.
 */
function moveRefusal(error, fallback, targetName = null) {
	const data = error?.response?.data || {};
	if (data.code === 'item_tag_carried') return itemTagCarriedRefusal(data.failures, { target: targetName });
	return apiErrorMessage(error, fallback);
}

/**
 * MOVE A CARD along the family line (docs/plans/sub-collections.md §5.6,
 * owner ruling D10). Choose where it goes — one of the collection's
 * sub-collections, or the collection above — and the modal asks the API
 * for a DRY RUN: the card and the child cards that go with it, their
 * records, the total, and what stops working. The primary names exactly
 * that (**Move 3 cards and 260 items**) and is the confirmation: nothing
 * moves before it is pressed.
 *
 *   targets = [{ _id, name, icon, color, direction: 'down' | 'up' }]
 */
export default function CardMoveModal({ open, onClose, card, sourceId, targets = [], onMoved }) {
	const { message } = App.useApp();
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = cardDefinitions?.cardsById || {};
	const refreshCards = cardDefinitions?.refresh;

	const [targetId, setTargetId] = useState(null);
	const [preview, setPreview] = useState(null);
	const [refusal, setRefusal] = useState(null);
	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState(false);
	// A dry run is a wait with no shape: a spinner, and none for the first 150 ms.
	const { visible: showSpinner } = useDelayedBusy(loading);
	const cardId = normalizeId(card?.id || card?._id);

	useEffect(() => {
		if (!open) return;
		setTargetId(targets.length === 1 ? normalizeId(targets[0]._id) : null);
		setPreview(null);
		setRefusal(null);
	// eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening
	}, [open]);

	const targetName = targets.find(entry => normalizeId(entry._id) === targetId)?.name || null;

	// The dry run, every time the destination changes.
	useEffect(() => {
		if (!open || !targetId || !cardId) { setPreview(null); return undefined; }
		let live = true;
		setLoading(true);
		setRefusal(null);
		setPreview(null);
		moveCard(cardId, targetId, { dryRun: true })
			.then(result => { if (live) setPreview(result); })
			.catch(error => { if (live) setRefusal(moveRefusal(error, 'This card cannot move there.', targetName)); })
			.finally(() => { if (live) setLoading(false); });
		return () => { live = false; };
	}, [open, targetId, cardId, targetName]);

	const target = targets.find(entry => normalizeId(entry._id) === targetId) || null;

	const submit = async () => {
		if (!preview || !targetId) return;
		setBusy(true);
		try {
			const result = await moveCard(cardId, targetId);
			if (typeof refreshCards === 'function') refreshCards();
			message.success(`${card?.name || 'The card'} moved to ${target?.name || 'its new collection'}.`);
			onMoved?.(result);
			onClose?.();
		} catch (error) {
			// The world moved between the dry run and the press: say so here.
			setRefusal(moveRefusal(error, 'The card could not move.', target?.name));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title={target?.direction === 'up' ? 'Move card up' : 'Move to sub-collection'}
			okText={preview ? moveVerb(preview) : 'Move'}
			okButtonProps={{ disabled: !preview || loading, loading: busy }}
			onOk={submit}
			onCancel={onClose}
			destroyOnHidden
			className="card-move-modal"
		>
			<RecordIdentity
				kicker="Card definition"
				badge={<RecordBadge kind="card" record={cardsById[cardId] || { name: card?.name }} title={card?.name || 'Card'} size="sm" />}
			/>
			<p className="card-move-modal__lead">
				{target?.direction === 'up'
					? 'Moving a card up makes it usable in the whole family. No record moves.'
					: 'Moving a card down takes it, the cards that extend it and their records into the sub-collection. It can then be used there only.'}
			</p>
			<Select
				className="card-move-modal__target"
				placeholder="Choose where it goes"
				value={targetId || undefined}
				onChange={setTargetId}
				aria-label="Move to"
				style={{ width: '100%' }}
				options={targets.map(entry => ({
					value: normalizeId(entry._id),
					label: <RecordBadge kind="collection" record={entry} title={entry.name} size="xs" />,
				}))}
			/>
			<div className="card-move-modal__result">
				{loading && showSpinner ? <Spinner size="sm" label="Working out what moves" showLabel /> : null}
				{refusal ? <Alert type="warning" showIcon message={refusal} /> : null}
				{preview ? <CardMovePreview preview={preview} cardsById={cardsById} sourceId={sourceId} /> : null}
			</div>
		</Modal>
	);
}
